import type { UIMessage } from 'ai';
import { getDisplayText } from '@flintmint/web-shared/utils/llm-message-utils';

import { useLLMContext } from '@/hooks/llm/use-llm-context/use-llm-context';
import { buildGenerateDocumentNameVariables } from '@/hooks/llm/prompt-invocation-builders';
import type { GenerateDocumentName } from './session/agent-session';
import { getContextParams } from './session/agent-tool-call-handlers';
import { isDefaultDocumentName, type PersistedDocument } from './document-utils';

export type AutoNameDocumentParams = {
  document?: PersistedDocument;
  content: string;
  messages: UIMessage[];
  isStale: () => boolean;
};

export async function autoNameDocument({
  document,
  generateDocumentName,
  getContext,
  content,
  messages,
  isStale,
}: AutoNameDocumentParams & {
  generateDocumentName: GenerateDocumentName;
  getContext: ReturnType<typeof useLLMContext>['getContext'];
}): Promise<string | undefined> {
  if (!document) return;

  try {
    const userMessages = messages
      .filter((message) => message.role === 'user')
      .map(getDisplayText)
      .filter(Boolean);
    const context = await getContext(getContextParams(document));
    const nameBeforeRequest = document.name;
    const isUnnamed = isDefaultDocumentName(nameBeforeRequest);
    if (!isUnnamed && (context.documentInstructions?.length ?? 0) === 0) {
      return;
    }

    const variables = buildGenerateDocumentNameVariables(content, {
      documentName: isUnnamed ? null : nameBeforeRequest,
      documentInstructions: context.documentInstructions,
      userMessages,
    }).variables;
    const result = await generateDocumentName(variables);
    if (!isUnnamed && result.violatedNameRule === null) {
      return;
    }

    const trimmed = result.name?.trim() ?? '';
    if (!trimmed || trimmed === nameBeforeRequest || document.getLatest().name !== nameBeforeRequest) {
      return;
    }

    if (isStale()) {
      return;
    }

    await document.incrementalPatch({ name: trimmed });
    return trimmed;
  } catch (err) {
    // Warn, not error: `captureConsoleIntegration` only reports console.error, and a failed name is
    // not worth an event — the backend captures `generateDocumentName` failures with the real error
    // class, and a failed local patch just leaves the document Untitled. Note this catch is wider
    // than that: a network failure or a throw from the variable building above also lands here and
    // goes unreported. See DM-1479.
    console.warn('Failed to auto-name document', err);
  }
}
